'use client';

import { useEffect, useMemo, useState } from 'react';

import { shuffle } from 'lodash';

import { MAX_WELCOME_SUGGESTIONS } from '@/common/constants';
import { useConfig } from '@/components/providers/config-provider';
import { readJsonFromSessionStorage, writeJsonToSessionStorage } from '@/lib/storage/safe-storage';
import type { ChatSuggestion } from '@/lib/types/tenant';

/**
 * Returns the subset of suggestions to display, applying session-persistent shuffling when
 * the tenant has `meta.randomizeSuggestions` enabled and the pool exceeds MAX_WELCOME_SUGGESTIONS.
 *
 * `pinned` suggestions (time-boxed campaign copy) are kept in front and held out of the shuffle —
 * their order is deliberate.
 *
 * @param suggestions - Full list of suggestions for the current locale, or null when not yet loaded.
 * @returns Ordered slice of up to MAX_WELCOME_SUGGESTIONS items. Stable within a session when
 *   randomization is active; truncated from the start otherwise.
 */
export function useRandomizedSuggestions(suggestions: ChatSuggestion[] | null): ChatSuggestion[] {
  const { tenantKey, meta } = useConfig();
  const randomize = meta?.randomizeSuggestions ?? false;
  const locale = meta?.language ?? 'en';

  // SSR and the hydrating first client render must produce the same order;
  // `shuffle` (Math.random) during render would diverge between the two and
  // throw React #418 (text content mismatch). Defer randomization to after
  // mount, where it only ever updates an already-hydrated tree.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return useMemo(() => {
    if (!suggestions || suggestions.length === 0) return [];

    const deterministic = suggestions.slice(0, MAX_WELCOME_SUGGESTIONS);
    if (!randomize || suggestions.length <= MAX_WELCOME_SUGGESTIONS || !mounted) {
      return deterministic;
    }

    const pinned = suggestions.filter(suggestion => suggestion.pinned);
    const shufflable = suggestions.filter(suggestion => !suggestion.pinned);
    const slots = MAX_WELCOME_SUGGESTIONS - pinned.length;
    if (slots <= 0) return pinned.slice(0, MAX_WELCOME_SUGGESTIONS);

    // Stores the whole shuffled pool, not the visible slice, so a slot freed mid-session (a
    // campaign ending) refills from the same order. Reconciled against what the server still
    // ships: drop what it doesn't, so a closed campaign can't return; append what's new, so the
    // row can't underfill.
    const storageKey = `bazak:suggestions:${tenantKey}:${locale}`;
    const stored = readJsonFromSessionStorage<ChatSuggestion[]>({ key: storageKey, fallback: [] });
    const shippable = new Set(shufflable.map(suggestion => suggestion.title));
    const remembered = stored.filter(suggestion => shippable.has(suggestion.title));

    const rememberedTitles = new Set(remembered.map(suggestion => suggestion.title));
    const missing = shuffle(shufflable.filter(s => !rememberedTitles.has(s.title)));
    const order = [...remembered, ...missing];
    if (missing.length > 0) writeJsonToSessionStorage({ key: storageKey, value: order });

    return [...pinned, ...order.slice(0, slots)];
  }, [suggestions, randomize, tenantKey, locale, mounted]);
}
